// PMax Asset Group Coverage — Creative Excellence section, deterministic check.
// Per-asset-group creative component counts against Tinuiti minimums, weakest first. Cells
// below the minimum are flagged so the gap is obvious at a glance.

import classNames from 'classnames';

import type { EvidenceProps } from './registry';

interface Counts {
  headline: number;
  longHeadline: number;
  description: number;
  image: number;
  video: number;
  logo: number;
  businessName: number;
}

interface AssetGroupRow {
  id?: string;
  name: string;
  campaignName: string;
  counts: Counts;
  coreGaps: string[];
  minorGaps: string[];
  hasCoreGap: boolean;
  hasAnyGap: boolean;
}

interface Minimums {
  headline: number;
  longHeadline: number;
  description: number;
  image: number;
  video: number;
  logo: number;
  businessName: number;
}

interface CoverageShape {
  totalAssetGroups: number;
  withCoreGapCount: number;
  withAnyGapCount: number;
  minimums: Minimums;
  assetGroups: AssetGroupRow[];
}

// A numeric coverage cell: shows n/min, flagged red when below the minimum.
const CountCell = ({ n, min }: { n: number; min: number }) => (
  <td className={classNames('evidence__td--right', { 'evidence__td--gap': n < min })}>
    {n}
    <span className='evidence__count-min'>/{min}</span>
  </td>
);

// A presence cell (video / logo / business name): ✓ when present, flagged ✗ when missing.
const PresenceCell = ({ n }: { n: number }) => {
  const ok = n > 0;
  return (
    <td className={classNames('evidence__td--center', { 'evidence__td--gap': !ok })}>
      {ok ? '✓' : '✗'}
    </td>
  );
};

export const PmaxAssetGroupCoverageEvidence = ({ evidence }: EvidenceProps) => {
  const e = evidence as unknown as CoverageShape;
  const m = e.minimums;

  return (
    <div className='evidence'>
      <section className='evidence__section'>
        <h4 className='evidence__section-title'>
          Coverage at a glance
          <span className='evidence__section-tag evidence__section-tag--context'>
            {e.totalAssetGroups} asset groups
          </span>
        </h4>
        <div className='audience-coverage'>
          <div className='audience-coverage__cell'>
            <div className='audience-coverage__num'>{e.totalAssetGroups.toLocaleString()}</div>
            <div className='audience-coverage__lbl'>Asset groups</div>
          </div>
          <div className='audience-coverage__cell'>
            <div className='audience-coverage__num'>{e.withCoreGapCount.toLocaleString()}</div>
            <div className='audience-coverage__lbl'>Missing core assets</div>
          </div>
          <div className='audience-coverage__cell'>
            <div className='audience-coverage__num'>{e.withAnyGapCount.toLocaleString()}</div>
            <div className='audience-coverage__lbl'>Any gap</div>
          </div>
        </div>
      </section>

      <section className='evidence__section'>
        <h4 className='evidence__section-title'>
          Per-asset-group coverage
          <span className='evidence__section-tag evidence__section-tag--context'>
            weakest first
          </span>
        </h4>
        <table className='evidence__table evidence__table--pmax-coverage'>
          <thead>
            <tr>
              <th>Asset group</th>
              <th className='evidence__th--right'>Headlines</th>
              <th className='evidence__th--right'>Long HL</th>
              <th className='evidence__th--right'>Desc</th>
              <th className='evidence__th--right'>Images</th>
              <th className='evidence__th--center'>Video</th>
              <th className='evidence__th--center'>Logo</th>
              <th className='evidence__th--center'>Name</th>
            </tr>
          </thead>
          <tbody>
            {e.assetGroups.map(g => (
              <tr
                key={g.id ?? g.name}
                className={classNames({ 'evidence__row--flagged': g.hasCoreGap })}
              >
                <td>
                  <div>{g.name}</div>
                  <div className='evidence__action-id'>{g.campaignName}</div>
                </td>
                <CountCell n={g.counts.headline} min={m.headline} />
                <CountCell n={g.counts.longHeadline} min={m.longHeadline} />
                <CountCell n={g.counts.description} min={m.description} />
                <CountCell n={g.counts.image} min={m.image} />
                <PresenceCell n={g.counts.video} />
                <PresenceCell n={g.counts.logo} />
                <PresenceCell n={g.counts.businessName} />
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <p className='evidence__footnote'>
        Tinuiti minimums per asset group: ≥{m.headline} headlines, ≥{m.longHeadline} long headlines,
        ≥{m.description} descriptions, ≥{m.image} images, and at least one video, logo, and business
        name. Flagged cells fall below the minimum. Complete creative is the most direct lever on Ad
        Strength and on how broadly Performance Max can serve.
      </p>
    </div>
  );
};
